import {useSearchParams} from "react-router";
import {useDispatch} from "react-redux";
import {
    OnboardingApi,
    UsersApi,
    Token,
    VerificationsApi,
} from "@devour/client";
import {login} from "@/redux/auth/authActions";
import {
    addError,
} from "@/redux/meta/metaActions";
import getConfig from "@/utils/getConfig";
import {removeReferralCode} from "@/redux/affiliateReferrals/affiliateActions";
import {removeAcquisitionSignals} from "@/redux/acquisition/acquisitionActions";
import * as Sentry from "@sentry/react";
import { waitForOverwolfAuthHandshake } from "./waitForOverwolfAuthHandshake";
import useOverwolfInterop from "./useOverwolfInterop";
import {useRef} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {USE_GET_USER_PROFILE_KEY} from "@/hooks/useGetUserProfile";
import {useGetAcquisitionSignals} from "@/hooks/useGetAcquisitionSignals";

/**
 * Retry a function with exponential backoff
 */
async function retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000,
): Promise<T> {
    let lastError: unknown;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            /*
             * Only retry on network errors
             * Chrome: "Failed to fetch", Safari/iOS: "Load failed"
             */
            const isNetworkError = error instanceof TypeError &&
                (error.message === "Failed to fetch" || error.message === "Load failed" || error.message.includes("network"));
            if (!isNetworkError || attempt === maxRetries - 1) {
                throw error;
            }
            // Exponential backoff: 1s, 2s, 4s
            const delay = baseDelay * 2 ** attempt;
            await new Promise<void>(resolve => {
                setTimeout(resolve, delay);
            });
        }
    }
    throw lastError;
}

/**
 * Hook to handle post-login functionality
 */
export default function useOnAuthThirdweb() {
    const dispatch = useDispatch();
    const acquisition = useGetAcquisitionSignals();
    const [searchParams] = useSearchParams();
    const signedUrlKey = searchParams.get("suk");
    const {isOnOverwolf, sendMessageToOW} = useOverwolfInterop();
    const isSubmittingRef = useRef(false);
    const queryClient = useQueryClient();

    /**
     * Reapply the user profile to redux
     * @param token
     * @param isFreshLogin
     */
    async function onReauthorize(token: Token, isFreshLogin: boolean) {
        if (!token) {
            return;
        }

        // grab the profile to save into redux (with retry for network failures)
        const userRes = await retryWithBackoff(
            () => new UsersApi(getConfig(token)).getUsersProfile(),
            3,
            1000,
        );

        // if its on overwolf, we need to propogate sending auth data to the OW app
        if (isOnOverwolf) {
            await waitForOverwolfAuthHandshake(token, userRes, sendMessageToOW, isFreshLogin);
        }


        //  save the token and profile into redux
        dispatch(login(token));
        queryClient.invalidateQueries({queryKey: [USE_GET_USER_PROFILE_KEY]});
        dispatch(removeReferralCode());
        /*
         * Cleared on the same beat as the referral code. Left in place, the
         * signals persist for 30 days and ride along on every later signup
         * from this browser — attributing a second account to a campaign that
         * never touched it.
         */
        dispatch(removeAcquisitionSignals());

        Sentry.setUser({
            email: userRes.user.email,
        });
        Sentry.setContext("userInfo", {...userRes});

        if (signedUrlKey) {
            await new OnboardingApi(getConfig(token)).updateOnboardingSignedUrlMergeUserSignedUrlKey({
                signedUrlKey: signedUrlKey,
            });
        }

        /*
         * thirdweb wallet auth (auth_endpoint connect) used to fire here on every
         * login/token-check, which pulled the web3 chunk into the global auth path
         * on every page. It now lives in WalletProvider (mounted only on wallet
         * surfaces): when a thirdweb-wallet user opens Stash / Devour Wallet / My
         * Rewards, WalletProvider reconnects the wallet — from its persisted
         * session, or fresh via the auth_endpoint + token when there is none. A
         * user who never opens a wallet surface never loads thirdweb.
         */
    }

    /**
     * Activate account using profile ID. Used for registrations using social media.
     *
     * All four acquisition signals are read from the persisted slice here rather than
     * from the caller, because every caller that hand-picked them picked `utmSource`
     * alone and booked paid spend as organic.
     * @param profileDataId
     * @param email
     * @param referralCode
     * @param utmSource overrides the persisted source only; the other three always come from the slice
     */
    async function onProfileRegistration(profileDataId: string, email: string, referralCode?: string, utmSource?: string): Promise<void> {
        if (isSubmittingRef.current) {
            return;
        }
        try {
            isSubmittingRef.current = true;
            // Use retry with backoff for network resilience (especially on mobile Safari)
            const token = await retryWithBackoff(
                () => new VerificationsApi().createPassportActivateProfileId({
                    id: profileDataId,
                    activateProfileBody: {
                        email: email,
                        referralCode: referralCode,
                        /*
                         * Caller's value first, persisted slice second. The
                         * social callback can land without the tagged URL the
                         * user originally arrived on.
                         */
                        ...acquisition,
                        utmSource: utmSource || acquisition.utmSource,
                    },
                }),
                3,
                1000,
            );

            await onReauthorize(token, true);

        } catch (err) {
            dispatch(await addError(err));
        } finally {
            isSubmittingRef.current = false;
        }

    }

    return {
        onReauthorize,
        onProfileRegistration,
    };
}
